import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import CircularProgress from 'components/shared/CircularProgress';
import { useProfile } from 'context/profile';
import useJobProcessProgress, { JobProgress } from 'hooks/useJobProcessProgress';
import { simpleDateAtTime } from 'lib/date';
import { JobProcessStatus } from 'models/JobProcess';
import { PERMISSIONS } from 'models/Profile';
import {
  adminBulkImportsQueryKeys,
  adminShowBulkImport,
  applyBulkImport,
  BulkImport,
  BulkImportStatus,
  BulkImportSummaryMessage,
  BulkImportType,
  cancelBulkImport,
  sendBulkImportInvites,
} from 'services/api/admin/bulkImports';
import { adminGroupsQueryKeys } from 'services/api/admin/groups';
import { adminLocationsQueryKeys } from 'services/api/admin/locations';
import { adminUsersQueryKeys } from 'services/api/admin/users';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { BodySmall, H3 } from 'styles/typography';
import CheckIcon from 'svg/CheckIcon';
import ChevronDown from 'svg/ChevronDown';

const STATUS_DOTS: Record<string, string> = {
  [BulkImportStatus.PREVIEWING]: colors.black25,
  [BulkImportStatus.PREVIEWED]: colors.blue100,
  [BulkImportStatus.APPLYING]: colors.yellow100,
  [BulkImportStatus.COMPLETED]: colors.green100,
  [BulkImportStatus.ERRORED]: colors.red100,
  [BulkImportStatus.CANCELED]: colors.black25,
};

const OUTCOME_STYLES: Record<string, { background: string; color: string; label: string }> = {
  will_create: { background: colors.green10, color: colors.green100, label: 'Will create' },
  will_match: { background: colors.blue10, color: colors.blue100, label: 'Will match' },
  error: { background: colors.red10, color: colors.red100, label: 'Error' },
  created: { background: colors.green10, color: colors.green100, label: 'Created' },
  matched: { background: colors.blue10, color: colors.blue100, label: 'Matched' },
  failed: { background: colors.red10, color: colors.red100, label: 'Failed' },
};

const MAX_ROW_CHIPS = 12;

export default function BulkImportDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useProfile();
  const canManage = profile.has(PERMISSIONS.adminBulkImportCreate);
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();
  const [invitesJobId, setInvitesJobId] = useState<string | null>(null);

  const showKey = adminBulkImportsQueryKeys.show(id as string);
  const refetch = () => queryClient.invalidateQueries({ queryKey: showKey });

  const { data: bulkImport, isLoading } = useQuery({
    queryKey: showKey,
    queryFn: () => adminShowBulkImport(id as string),
  });

  const applyMutation = useMutation({
    mutationFn: () => applyBulkImport(id as string),
    onSuccess: (res) => queryClient.setQueryData(showKey, res),
    onError: () => addToast({ text: 'Something went wrong starting the import. Please try again.' }),
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelBulkImport(id as string),
    onSuccess: (res) => {
      queryClient.setQueryData(showKey, res);
      addToast({ text: 'Import canceled.' });
      navigate('/admin/bulk-import');
    },
    onError: () => addToast({ text: 'Something went wrong canceling the import. Please try again.' }),
  });

  // Mount-time refetch covers the admin list pages today (no staleTime
  // configured), but if a global staleTime ever lands, those lists must still
  // reflect the newly imported records right away.
  const invalidateImported = () => {
    queryClient.invalidateQueries({ queryKey: adminUsersQueryKeys.all });
    queryClient.invalidateQueries({ queryKey: adminLocationsQueryKeys.index });
    queryClient.invalidateQueries({ queryKey: adminGroupsQueryKeys.index });
  };

  const applyProgress = useJobProcessProgress(bulkImport?.applying ? bulkImport.jobProcessId : null, () => {
    refetch();
    invalidateImported();
  });
  const invitesProgress = useJobProcessProgress(invitesJobId, () => {
    setInvitesJobId(null);
    addToast({ text: 'Invites sent.' });
    invalidateImported();
  });
  // The hook's status is local state, so this is true only when the apply
  // finished during this visit — a fresh page load shows no success banner.
  const applySucceededHere = applyProgress.status === JobProcessStatus.Completed;

  const invitesMutation = useMutation({
    mutationFn: () => sendBulkImportInvites(id as string),
    onSuccess: (res) => {
      setInvitesJobId(res.jobProcessId);
      addToast({ text: 'Sending invites…' });
    },
    onError: () => addToast({ text: 'Something went wrong sending invites. Please try again.' }),
  });

  if (isLoading || !bulkImport) {
    return (
      <SpinnerContainer>
        <CircularProgress />
      </SpinnerContainer>
    );
  }

  const showRows =
    bulkImport.previewed || bulkImport.applying || bulkImport.completed || bulkImport.errored || bulkImport.canceled;

  return (
    <AdminFormPage>
      <BackLink type='button' onClick={() => navigate('/admin/bulk-import')}>
        ← All imports
      </BackLink>
      <HeaderRow>
        <div>
          <H3>{bulkImport.isUsers ? 'Bulk user import' : 'Bulk location import'}</H3>
          <MetaLine>
            {bulkImport.typeLabel}
            {bulkImport.createdAt ? ` · ${simpleDateAtTime(bulkImport.createdAt)}` : ''}
            {bulkImport.isUsers ? ` · Invites ${bulkImport.sendInvite ? 'sent on create' : 'deferred'}` : ''}
          </MetaLine>
        </div>
        <StatusBadge status={bulkImport.status} />
      </HeaderRow>

      {bulkImport.fileErrors.length > 0 && <Banner>{bulkImport.fileErrors.join('; ')}</Banner>}
      {bulkImport.errored && (
        <Banner>The import stopped before finishing. Rows without an outcome were not applied.</Banner>
      )}

      <StatTiles bulkImport={bulkImport} />
      <IssuesPanel messages={bulkImport.summary.messages ?? []} />

      {showRows && <RowsTable bulkImport={bulkImport} />}

      {bulkImport.previewed && canManage && (
        <Actions>
          <Button
            variant='ghost'
            onClick={() => cancelMutation.mutate()}
            disabled={cancelMutation.isPending || applyMutation.isPending}
            loading={cancelMutation.isPending}>
            Cancel import
          </Button>
          <Button
            onClick={() => applyMutation.mutate()}
            disabled={bulkImport.actionableRowCount === 0 || applyMutation.isPending || cancelMutation.isPending}
            loading={applyMutation.isPending}>
            Apply {bulkImport.actionableRowCount} {bulkImport.actionableRowCount === 1 ? 'row' : 'rows'}
          </Button>
        </Actions>
      )}

      {bulkImport.canceled && <QuietNote>This import was canceled before any rows were applied.</QuietNote>}

      {bulkImport.applying && (
        <WorkingIndicator>
          <CircularProgress size={16} thickness={2} color={colors.blue100} />
          <BodySmall>Applying import…{progressCount(applyProgress.progress)}</BodySmall>
        </WorkingIndicator>
      )}

      {bulkImport.completed && applySucceededHere && (
        <SuccessIndicator>
          <SuccessBadge>
            <CheckIcon width={10} height={8} />
          </SuccessBadge>
          <SuccessText>Import complete</SuccessText>
        </SuccessIndicator>
      )}

      {bulkImport.isUsers &&
        bulkImport.completed &&
        canManage &&
        !bulkImport.sendInvite &&
        (bulkImport.summary.apply?.created ?? 0) > 0 && (
          <Actions>
            {invitesJobId ? (
              <WorkingIndicator>
                <CircularProgress size={16} thickness={2} color={colors.blue100} />
                <BodySmall>Sending invites…{progressCount(invitesProgress.progress)}</BodySmall>
              </WorkingIndicator>
            ) : (
              <Button
                variant='outline-primary'
                onClick={() => invitesMutation.mutate()}
                disabled={invitesMutation.isPending}
                loading={invitesMutation.isPending}>
                Send invites
              </Button>
            )}
          </Actions>
        )}
    </AdminFormPage>
  );
}

function progressCount(progress: JobProgress | null) {
  if (!progress?.total) return '';

  return ` ${progress.processed} of ${progress.total}`;
}

function StatusBadge({ status }: { status: string }) {
  return (
    <StatusContainer>
      <StatusDot color={STATUS_DOTS[status] ?? colors.black25} />
      <StatusText>{status}</StatusText>
    </StatusContainer>
  );
}

function StatTiles({ bulkImport }: { bulkImport: BulkImport }) {
  const { summary } = bulkImport;
  const apply = summary.apply;

  const tiles = apply
    ? [
        { label: 'Processed', value: apply.processed ?? 0, color: colors.black100 },
        { label: 'Created', value: apply.created ?? 0, color: colors.green100 },
        { label: 'Matched', value: apply.matched ?? 0, color: colors.blue100 },
        { label: 'Failed', value: apply.failed ?? 0, color: colors.red100 },
      ]
    : [
        { label: 'Total rows', value: summary.totalRows ?? 0, color: colors.black100 },
        { label: 'Will create', value: summary.willCreate ?? 0, color: colors.green100 },
        { label: 'Will match', value: summary.willMatch ?? 0, color: colors.blue100 },
        { label: 'Errors', value: summary.error ?? 0, color: colors.red100 },
      ];

  return (
    <Tiles>
      {tiles.map((tile) => (
        <Tile key={tile.label}>
          <TileLabel>{tile.label}</TileLabel>
          <TileValue color={tile.value > 0 ? tile.color : colors.black25}>{tile.value.toLocaleString()}</TileValue>
        </Tile>
      ))}
    </Tiles>
  );
}

function IssuesPanel({ messages }: { messages: BulkImportSummaryMessage[] }) {
  const [open, setOpen] = useState(false);

  if (messages.length === 0) return null;

  const errors = messages.filter((m) => m.type === 'error');
  const warnings = messages.filter((m) => m.type === 'warning');
  const parts = [
    errors.length > 0 && `${errors.length} ${errors.length === 1 ? 'error' : 'errors'}`,
    warnings.length > 0 && `${warnings.length} ${warnings.length === 1 ? 'warning' : 'warnings'}`,
  ].filter(Boolean);

  return (
    <IssuesCard>
      <IssuesHeader type='button' onClick={() => setOpen(!open)} aria-expanded={open}>
        <IssuesTitle>Needs attention</IssuesTitle>
        <IssuesCounts>{parts.join(' · ')}</IssuesCounts>
        <Chevron $open={open} />
      </IssuesHeader>
      {open && (
        <IssuesList>
          {[...errors, ...warnings].map((msg) => (
            <IssueRow key={`${msg.type}-${msg.message}`}>
              <SeverityDot color={msg.type === 'error' ? colors.red100 : colors.yellow100} />
              <IssueBody>
                <IssueText>{msg.message}</IssueText>
                <RowChips>
                  {msg.rowNumbers.slice(0, MAX_ROW_CHIPS).map((n) => (
                    <RowChip key={n}>row {n}</RowChip>
                  ))}
                  {msg.rowNumbers.length > MAX_ROW_CHIPS && (
                    <MoreChips>+{msg.rowNumbers.length - MAX_ROW_CHIPS} more</MoreChips>
                  )}
                </RowChips>
              </IssueBody>
            </IssueRow>
          ))}
        </IssuesList>
      )}
    </IssuesCard>
  );
}

// Per-type row columns; # / Outcome / Detail are shared.
const ROW_COLUMNS: Record<string, { header: string; value: (data: Record<string, any>) => string }[]> = {
  [BulkImportType.USERS]: [
    { header: 'Name', value: (data) => data.name },
    { header: 'Email', value: (data) => data.email },
    { header: 'Client', value: (data) => data.client },
  ],
  [BulkImportType.LOCATIONS]: [
    { header: 'Location', value: (data) => data.locationName },
    { header: 'Client', value: (data) => data.client },
    { header: 'Timezone', value: (data) => data.timezone },
    // Resolved display names; error rows never resolve, so show the raw
    // column value (typos and all).
    { header: 'Group Types', value: (data) => data.groupTypeNames?.join(', ') || (data.groupTypes ?? '') },
  ],
  [BulkImportType.PLAN_TYPES]: [
    { header: 'Plan Type', value: (data) => data.planType },
    { header: 'Plan', value: (data) => data.plan },
    // Resolved names; error rows never resolve, so show the raw column value.
    { header: 'Clients', value: (data) => data.clientNames?.join(', ') || (data.clients ?? '') },
  ],
};

function RowsTable({ bulkImport }: { bulkImport: BulkImport }) {
  const columns = ROW_COLUMNS[bulkImport.importType] ?? ROW_COLUMNS[BulkImportType.USERS];

  return (
    <TableCard>
      <TableScroll>
        <Table>
          <thead>
            <tr>
              <Th>#</Th>
              {columns.map((column) => (
                <Th key={column.header}>{column.header}</Th>
              ))}
              <Th>Outcome</Th>
              <Th>Detail</Th>
            </tr>
          </thead>
          <tbody>
            {bulkImport.rows.map((row) => {
              const outcome = row.result ?? row.decision;
              const style = OUTCOME_STYLES[outcome];
              return (
                <tr key={row.id}>
                  <RowNumberTd>{row.rowNumber}</RowNumberTd>
                  {columns.map((column) => (
                    <Td key={column.header}>{column.value(row.data)}</Td>
                  ))}
                  <Td>
                    <OutcomePill
                      $background={style?.background ?? colors.black5}
                      $color={style?.color ?? colors.black50}>
                      {style?.label ?? outcome}
                    </OutcomePill>
                  </Td>
                  <DetailTd $error={!!row.error}>{row.error ?? (row.data.warnings ?? []).join('; ')}</DetailTd>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableScroll>
    </TableCard>
  );
}

const BackLink = styled.button`
  display: inline-block;
  margin-bottom: 12px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  line-height: 16px;
  color: ${colors.blue100};

  &:hover {
    text-decoration: underline;
  }
`;

const SpinnerContainer = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const MetaLine = styled.div`
  margin-top: 4px;
  font-size: 12px;
  line-height: 16px;
  color: ${colors.black50};
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border: 1px solid ${colors.black10};
  border-radius: 24px;
  background: ${colors.white};
`;

const StatusDot = styled.span<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  flex-shrink: 0;
`;

const StatusText = styled.span`
  font-size: 12px;
  text-transform: capitalize;
  color: ${colors.black75};
`;

const Banner = styled.div`
  margin-top: 16px;
  padding: 12px 16px;
  border: 1px solid ${colors.red25};
  border-radius: 6px;
  background: ${colors.red5};
  color: ${colors.red100};
  font-size: 14px;
  line-height: 20px;
`;

const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-top: 20px;

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Tile = styled.div`
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  background: ${colors.white};
  padding: 14px 16px;
`;

const TileLabel = styled.div`
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${colors.black50};
`;

const TileValue = styled.div<{ color: string }>`
  margin-top: 6px;
  font-size: 22px;
  line-height: 28px;
  font-weight: var(--font-weight-bold);
  font-variant-numeric: tabular-nums;
  color: ${({ color }) => color};
`;

const IssuesCard = styled.div`
  margin-top: 12px;
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  background: ${colors.white};
  overflow: hidden;
`;

const IssuesHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${colors.black3};
  }
`;

const IssuesTitle = styled.span`
  font-size: 14px;
  font-weight: var(--font-weight-bold);
  color: ${colors.black100};
`;

const IssuesCounts = styled.span`
  flex: 1;
  font-size: 12px;
  color: ${colors.black50};
`;

const Chevron = styled(ChevronDown)<{ $open: boolean }>`
  transform: ${({ $open }) => ($open ? 'rotate(0deg)' : 'rotate(180deg)')};
  transform-origin: center;
  flex-shrink: 0;
`;

const IssuesList = styled.div`
  border-top: 1px solid ${colors.black5};
`;

const IssueRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px 16px;

  & + & {
    border-top: 1px solid ${colors.black5};
  }
`;

const SeverityDot = styled.span<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  flex-shrink: 0;
  margin-top: 6px;
`;

const IssueBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const IssueText = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: ${colors.black100};
`;

const RowChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
`;

const RowChip = styled.span`
  font-size: 10px;
  line-height: 14px;
  padding: 1px 8px;
  border-radius: 24px;
  background: ${colors.black5};
  color: ${colors.black75};
  font-variant-numeric: tabular-nums;
`;

const MoreChips = styled.span`
  font-size: 10px;
  line-height: 16px;
  color: ${colors.black50};
`;

const TableCard = styled.div`
  margin-top: 12px;
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  background: ${colors.white};
  overflow: hidden;
`;

const TableScroll = styled.div`
  overflow: auto;
  max-height: 440px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  position: sticky;
  top: 0;
  z-index: 1;
  text-align: left;
  padding: 10px 12px;
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${colors.black50};
  /* Solid so scrolled rows don't show through the sticky header. */
  background: ${colors.white};
  box-shadow: inset 0 -1px 0 ${colors.black10};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px 12px;
  font-size: 13px;
  line-height: 18px;
  color: ${colors.black100};
  border-bottom: 1px solid ${colors.black5};

  tr:last-child > & {
    border-bottom: none;
  }

  tr:hover > & {
    background: ${colors.black3};
  }
`;

const RowNumberTd = styled(Td)`
  color: ${colors.black25};
  font-variant-numeric: tabular-nums;
  width: 40px;
`;

const DetailTd = styled(Td)<{ $error: boolean }>`
  color: ${({ $error }) => ($error ? colors.red100 : colors.black50)};
`;

const OutcomePill = styled.span<{ $background: string; $color: string }>`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 24px;
  font-size: 11px;
  font-weight: var(--font-weight-bold);
  background: ${({ $background }) => $background};
  color: ${({ $color }) => $color};
  white-space: nowrap;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
`;

const QuietNote = styled(BodySmall)`
  margin-top: 16px;
  color: ${colors.black50};
`;

const WorkingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
`;

const SuccessIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
`;

const SuccessBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${colors.green100};
  flex-shrink: 0;
`;

const SuccessText = styled(BodySmall)`
  color: ${colors.green100};
  font-weight: var(--font-weight-bold);
`;
